import type { ReactNode } from 'react';
import {
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  Dialog as HeadlessDialog,
} from '@headlessui/react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'md' | 'lg' | 'xl';
}

const WIDTHS = { md: 'sm:max-w-lg', lg: 'sm:max-w-2xl', xl: 'sm:max-w-4xl' } as const;

/** Modal dialog: a bottom sheet on phones, a centred panel from tablets up. */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
}: DialogProps) {
  const { t } = useTranslation();
  return (
    <HeadlessDialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-ink/35 transition-opacity duration-200 data-closed:opacity-0"
      />
      <div className="fixed inset-0 flex items-end justify-center sm:items-center sm:p-6">
        <DialogPanel
          transition
          className={cn(
            'flex max-h-[92dvh] w-full flex-col rounded-t-card bg-surface shadow-pop sm:rounded-card',
            'transition duration-200 ease-out data-closed:translate-y-6 data-closed:opacity-0',
            WIDTHS[size],
          )}
        >
          <header className="flex items-start justify-between gap-4 border-b border-line px-5 py-4 sm:px-6">
            <div className="min-w-0">
              <DialogTitle className="text-section font-semibold">{title}</DialogTitle>
              {description && <p className="mt-1 text-sm text-muted">{description}</p>}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="-mr-2 rounded-control p-2 text-ink-2 hover:bg-ground"
              aria-label={t('actions.close')}
            >
              <X className="size-5" />
            </button>
          </header>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">{children}</div>
          {footer && (
            <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:px-6">
              {footer}
            </footer>
          )}
        </DialogPanel>
      </div>
    </HeadlessDialog>
  );
}

/** Side panel for secondary content (history, details). */
export function Drawer({
  open,
  onClose,
  title,
  description,
  children,
}: Omit<DialogProps, 'footer' | 'size'>) {
  const { t } = useTranslation();
  return (
    <HeadlessDialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-ink/25 transition-opacity duration-200 data-closed:opacity-0"
      />
      <div className="fixed inset-y-0 right-0 flex w-full max-w-md">
        <DialogPanel
          transition
          className="flex w-full flex-col bg-surface shadow-pop transition duration-200 ease-out data-closed:translate-x-8 data-closed:opacity-0"
        >
          <header className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
            <div className="min-w-0">
              <DialogTitle className="text-section font-semibold">{title}</DialogTitle>
              {description && <p className="mt-1 text-sm text-muted">{description}</p>}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="-mr-2 rounded-control p-2 text-ink-2 hover:bg-ground"
              aria-label={t('actions.close')}
            >
              <X className="size-5" />
            </button>
          </header>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        </DialogPanel>
      </div>
    </HeadlessDialog>
  );
}
